import axios, { AxiosError } from 'axios';

import store from 'store/store';
import { resetTickets } from 'store/ticket-service/ticketSlice';
import { setDeniedAccessErrorOpen } from 'store/user-service/userSlice';

import { VaultService } from 'utils/storage';

import { getDevOrProdUrl } from 'core/helpers/get-dev-or-prod-url';
import { UserData } from 'core/types/base';

import { setRequestInterceptorWithToken } from './interceptorHelpers';

const storage = new VaultService();

const user: any = storage.getItem('user');
const refreshToken = user && user.refreshToken;

export const baseURL = getDevOrProdUrl('dev', 'gateway');

const axiosConfig: Record<string, any> = {
	headers: { 'Content-Type': 'application/json' },
	baseURL,
	refreshToken: refreshToken,
};

const instance = axios.create(axiosConfig);

type PendingRequestCallback = () => void;

let isRefreshing = false;
const pendingRequests: PendingRequestCallback[] = [];

// Function to refresh the token
export const refreshAccessToken = async () => {
	const user = storage.getItem('user') as UserData;

	if (isRefreshing) {
		return new Promise<void>((resolve) => {
			const wrapper: PendingRequestCallback = () => {
				resolve();
			};

			pendingRequests.push(wrapper);
		});
	}

	isRefreshing = true;

	try {
		const data = {
			jwt: storage.getItem('token'),
			refreshToken: user?.refreshToken || '',
		};

		const refreshResponse = await instance.post('/auth/refreshJwtToken', data);
		storage.setItem('token', refreshResponse.data);
		window.dispatchEvent(new Event('storage'));
		pendingRequests.forEach((callback) => callback());

		pendingRequests.length = 0;
	} catch (error) {
		storage.removeItem('token');
		console.log(error);
	} finally {
		isRefreshing = false;
	}
};

// Interceptors

setRequestInterceptorWithToken(instance);

instance.interceptors.response.use(
	(response) => response,
	async (error: AxiosError) => {
		if (error && error.response) {
			switch (error.response.status) {
				case 401:
					// if refresh token request failed, clear old token and log out user
					if (error.request.responseURL.includes('refreshJwtToken')) {
						store.dispatch(resetTickets());
						Object.keys(localStorage).forEach((key) => {
							if (key !== 'i18nextLng') {
								localStorage.removeItem(key);
							}
						});
						window.location.href = `${window.location.href}sign-in`;
					}

					// Refresh the token and retry the request
					try {
						if (storage.getItem('token')) {
							await refreshAccessToken();

							if (error.config) return instance(error.config);
						}
					} catch (refreshError) {
						const axiosError = refreshError as AxiosError;
						return Promise.reject(axiosError.response);
					}
					break;
				case 403:
					if (!error?.config?.url?.includes('auth/login')) {
						store.dispatch(setDeniedAccessErrorOpen(true));
					}
					break;
				default:
					return error.response;
			}

			return Promise.reject(error.response);
		} else {
			return Promise.reject(error.response);
		}

		return;
	}
);

export default instance;
